import * as z from 'zod';
import type { ZodOpenApiPathsObject } from 'zod-openapi';
import { ReasonSchema } from './catalogue.js';
import { problemResponse } from './common.js';
import { CustomerTypeSchema } from './customers.js';
import { PriceBreakdownSchema, QuoteSchema } from './pricing.js';

/** Orders, invoices and returns (spec §3, §6; plan 04). Money in kopecks. */

export const ORDER_STATUSES = [
  'draft',
  'awaiting_payment',
  'confirmed',
  'shipped',
  'delivered',
  'completed',
  'cancelled',
  'returned',
] as const;
export const OrderStatusSchema = z.enum(ORDER_STATUSES).meta({ id: 'OrderStatus' });
export const PaymentStatusSchema = z
  .enum(['unpaid', 'partially_paid', 'paid', 'overpaid', 'refunded'])
  .meta({ id: 'PaymentStatus' });
export const PaymentMethodSchema = z
  .enum(['card', 'bank_transfer', 'cod'])
  .meta({ id: 'PaymentMethod' });
export const ChannelSchema = z.enum(['woo', 'manager', 'legacy']).meta({ id: 'OrderChannel' });
export const DeliveryMethodSchema = z.enum(['nova_post', 'pickup']).meta({ id: 'DeliveryMethod' });
export const OrderActionSchema = z
  .enum(['edit', 'place', 'cancel', 'ship', 'deliver', 'return'])
  .meta({ id: 'OrderAction' });
export const OrderViewSchema = z
  .enum(['awaiting_payment', 'to_ship', 'cod_unsettled'])
  .meta({ id: 'OrderSavedView' });

const kopecks = z.number().int();
const text = (max: number) => z.string().trim().max(max);

export const DeliveryAddressSchema = z
  .object({
    recipientName: text(200).min(1),
    recipientPhone: text(40).min(1),
    city: text(200).nullable(),
    warehouse: text(300).nullable(),
    npCityRef: text(64).nullable(),
    npWarehouseRef: text(64).nullable(),
  })
  .meta({ id: 'DeliveryAddress' });

export const OrderSummarySchema = z
  .object({
    id: z.uuid(),
    number: z.number().int(),
    customerId: z.uuid(),
    customerName: z.string(),
    customerType: CustomerTypeSchema,
    channel: ChannelSchema,
    wooOrderId: z
      .number()
      .int()
      .nullable()
      .meta({ description: 'The WooCommerce order number (store orders)' }),
    status: OrderStatusSchema,
    paymentMethod: PaymentMethodSchema.nullable(),
    paymentStatus: PaymentStatusSchema,
    total: kopecks,
    attention: z.array(z.string()).meta({
      description:
        'What a manager should check, e.g. a store order charged at prices that differ from the CRM (spec §7.7); empty when nothing',
    }),
    placedAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .meta({ id: 'OrderSummary' });

export const OrderPageSchema = z
  .object({ items: z.array(OrderSummarySchema), total: z.number().int() })
  .meta({ id: 'OrderPage' });

export const OrderLineSchema = z
  .object({
    id: z.uuid(),
    productId: z.uuid(),
    sku: z.string(),
    name: z.string(),
    qty: z.number().int(),
    basePrice: kopecks,
    unitPrice: kopecks,
    lineTotal: kopecks,
    breakdown: PriceBreakdownSchema,
    override: z
      .object({
        mode: z.enum(['price', 'percent']),
        value: z.number().meta({ description: 'Kopecks for price, percent for percent' }),
        reason: z.string(),
      })
      .nullable(),
  })
  .meta({ id: 'OrderLine' });

export const InvoiceSchema = z
  .object({
    id: z.uuid(),
    number: z.string(),
    kind: z.enum(['initial', 'top_up', 'credit_note']),
    amount: kopecks,
    outstanding: kopecks,
    dueDate: z.iso.date(),
    status: z.enum(['open', 'paid', 'cancelled']),
    issuedAt: z.iso.datetime(),
  })
  .meta({ id: 'Invoice' });

export const OrderPaymentSchema = z
  .object({
    id: z.uuid(),
    direction: z.enum(['in', 'refund']),
    method: PaymentMethodSchema,
    provider: z.string(),
    amount: kopecks,
    reference: z.string().nullable(),
    reason: z.string().nullable(),
    receivedAt: z.iso.datetime(),
  })
  .meta({ id: 'OrderPayment' });

export const OrderHistoryEntrySchema = z
  .object({
    at: z.iso.datetime(),
    action: z.string(),
    userName: z.string().nullable().meta({ description: 'null = the system' }),
    reason: z.string().nullable(),
    before: z.record(z.string(), z.unknown()).nullable(),
    after: z.record(z.string(), z.unknown()).nullable(),
  })
  .meta({ id: 'OrderHistoryEntry' });

export const OrderSchema = OrderSummarySchema.extend({
  quoteId: z.uuid().nullable(),
  deferred: z.boolean(),
  deliveryMethod: DeliveryMethodSchema,
  deliveryAddress: DeliveryAddressSchema.nullable(),
  comment: z.string().nullable(),
  subtotal: kopecks,
  discountTotal: kopecks,
  pointsRedeemed: z.number().int(),
  pointsValue: kopecks,
  returnWindowDays: z
    .number()
    .int()
    .nullable()
    .meta({ description: 'Return window the order was placed under (days); null before placing' }),
  returnUntil: z.iso.date().nullable().meta({
    description:
      'Kyiv date the return window closes (delivery date + window); null until delivered',
  }),
  shippedAt: z.iso.datetime().nullable(),
  deliveredAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  cancelledAt: z.iso.datetime().nullable(),
  returnedAt: z.iso.datetime().nullable(),
  updatedAt: z.iso.datetime(),
  lines: z.array(OrderLineSchema),
  invoices: z.array(InvoiceSchema),
  payments: z.array(OrderPaymentSchema),
  return: z
    .object({
      reason: z.string(),
      refundAmount: kopecks,
      fromStatus: OrderStatusSchema,
      createdAt: z.iso.datetime(),
    })
    .nullable(),
  history: z.array(OrderHistoryEntrySchema),
  actions: z.array(OrderActionSchema).meta({ description: 'Next steps the status allows' }),
}).meta({ id: 'Order' });

export const OrderQuerySchema = z.object({
  view: OrderViewSchema.optional(),
  search: z.string().max(100).optional().meta({ description: 'Order number or customer name' }),
  status: OrderStatusSchema.optional(),
  paymentStatus: PaymentStatusSchema.optional(),
  paymentMethod: PaymentMethodSchema.optional(),
  channel: ChannelSchema.optional(),
  customerType: CustomerTypeSchema.optional(),
  customerId: z.uuid().optional(),
  from: z.iso.date().optional().meta({ description: 'Created on or after (Kyiv date)' }),
  to: z.iso.date().optional().meta({ description: 'Created on or before (Kyiv date)' }),
  limit: z.coerce.number().int().positive().max(200).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});

export const CreateOrderSchema = z
  .object({ customerId: z.uuid(), comment: text(1000).nullable().optional() })
  .meta({ id: 'CreateOrder' });

export const UpdateOrderSchema = z
  .object({
    paymentMethod: PaymentMethodSchema.nullable().optional(),
    deliveryMethod: DeliveryMethodSchema.optional(),
    deliveryAddress: DeliveryAddressSchema.nullable().optional(),
    comment: text(1000).nullable().optional(),
    reason: ReasonSchema.optional().meta({ description: 'Required once the order is placed' }),
  })
  .meta({ id: 'UpdateOrder' });

export const SetOrderLinesSchema = z
  .object({
    lines: z
      .array(
        z.object({
          sku: z.string().trim().min(1).max(64),
          qty: z.number().int().min(1).max(10_000),
          override: z
            .object({
              mode: z.enum(['price', 'percent']),
              value: z.number().min(0).max(100_000_000),
              reason: ReasonSchema,
            })
            .nullable()
            .optional(),
        }),
      )
      .max(200),
    reason: ReasonSchema.optional().meta({ description: 'Required once the order is placed' }),
  })
  .meta({ id: 'SetOrderLines' });

export const PlaceOrderSchema = z
  .object({
    paymentMethod: PaymentMethodSchema,
    deferred: z.boolean().optional().meta({ description: 'B2B bank transfer on deferred terms' }),
  })
  .meta({ id: 'PlaceOrder' });

export const ShipOrderSchema = z
  .object({ ttn: text(40).nullable().optional() })
  .meta({ id: 'ShipOrder' });

export const OrderReasonSchema = z.object({ reason: ReasonSchema }).meta({ id: 'OrderReason' });

/** Q-points on an order (LOY-04). */
export const SetOrderPointsSchema = z
  .object({
    points: z.number().int().min(0).max(100_000_000),
    reason: ReasonSchema.optional().meta({ description: 'Required once the order is placed' }),
  })
  .meta({ id: 'SetOrderPoints' });

export const OrderPointsSchema = z
  .object({
    pointsRedeemed: z.number().int(),
    pointsValue: kopecks,
    maxPoints: z
      .number()
      .int()
      .meta({ description: 'Most points this order may take now (its own count as available)' }),
    pointValue: kopecks.meta({ description: 'Kopecks one point is worth now' }),
    toEarn: z.number().int().meta({ description: 'Points the order earns when paid (or earned)' }),
    earned: z
      .object({
        points: z.number().int(),
        status: z.enum(['pending', 'active', 'cancelled']),
        expiresAt: z.iso.datetime().nullable(),
      })
      .nullable(),
  })
  .meta({ id: 'OrderPoints' });

// ---------- Store orders (ORD-15) ----------

export const WooOrderSchema = z
  .object({
    wooOrderId: z.number().int().positive(),
    quoteId: z.uuid().meta({ description: 'The quote the customer saw at checkout' }),
    customerId: z.uuid().nullable().meta({
      description: 'The linked customer the store priced for; null when priced as a guest',
    }),
    buyer: z
      .object({
        wooCustomerId: z.number().int().positive().nullable(),
        fullName: text(200).min(1),
        phone: text(40).nullable(),
        email: text(254).nullable(),
      })
      .meta({
        description: 'Who is buying: finds or creates the customer when customerId is null',
      }),
    lines: z
      .array(z.object({ sku: text(64).min(1), qty: z.number().int().min(1).max(10_000) }))
      .min(1)
      .max(200),
    paymentMethod: z.enum(['cod', 'card']).meta({
      description:
        'Cash on delivery, or card through Monobank in Woo (the order then awaits its payment, reported later through /orders/from-woo/charged)',
    }),
    deliveryMethod: DeliveryMethodSchema,
    deliveryAddress: DeliveryAddressSchema.nullable(),
    comment: text(2000).nullable(),
    pointsRequested: z.number().int().min(0).max(100_000_000).default(0).meta({
      description:
        'Q-points the customer applied, as in the quote (a linked, logged-in customer only)',
    }),
  })
  .meta({ id: 'WooOrder' });

/** ORD-17 (spec §7.7): a store order as Woo charged it, sent after payment in the background. */
export const ChargedWooOrderSchema = z
  .object({
    wooOrderId: z.number().int().positive(),
    quoteId: z.uuid().nullable().meta({
      description: 'The quote the cart was priced from, or null when DeliCRM did not answer',
    }),
    customerId: z.uuid().nullable(),
    buyer: WooOrderSchema.shape.buyer,
    lines: z
      .array(
        z.object({
          sku: text(64).min(1),
          qty: z.number().int().min(1).max(10_000),
          unitPrice: kopecks.meta({ description: 'What the store charged per unit, kopecks' }),
        }),
      )
      .min(1)
      .max(200),
    pointsRedeemed: z.number().int().min(0).max(100_000_000),
    pointsValue: kopecks.meta({ description: 'The Q-points discount the store gave, kopecks' }),
    total: kopecks.meta({ description: 'What the store charged in total, kopecks' }),
    paymentMethod: z.enum(['card', 'cod']),
    payment: z
      .object({
        provider: z.enum(['monobank']),
        externalId: text(100)
          .min(1)
          .meta({ description: "The provider's id (Monobank invoice id)" }),
        amount: kopecks,
        receivedAt: z.iso.datetime(),
      })
      .nullable()
      .meta({ description: 'The card payment; null for cash on delivery' }),
    deliveryMethod: DeliveryMethodSchema,
    deliveryAddress: DeliveryAddressSchema.nullable(),
    comment: text(2000).nullable(),
  })
  .meta({ id: 'ChargedWooOrder' });

export const WooOrderResultSchema = z
  .object({
    orderId: z.uuid(),
    number: z.number().int(),
    status: OrderStatusSchema,
    paymentStatus: PaymentStatusSchema,
    total: kopecks,
    created: z.boolean().meta({ description: 'false: this Woo order was already pushed' }),
  })
  .meta({ id: 'WooOrderResult' });

export const ChargedWooOrderResultSchema = WooOrderResultSchema.extend({
  attention: z.array(z.string()).meta({ description: 'What a manager will be asked to check' }),
}).meta({ id: 'ChargedWooOrderResult' });

export const WooOrderRequoteSchema = z
  .object({
    reason: z.enum(['expired', 'mismatch']),
    quote: QuoteSchema.meta({
      description: 'The fresh price to show the customer before retrying',
    }),
  })
  .meta({ id: 'WooOrderRequote' });

export type WooOrderRequest = z.output<typeof WooOrderSchema>;
export type ChargedWooOrderRequest = z.infer<typeof ChargedWooOrderSchema>;
export type ChargedWooOrderResultDto = z.infer<typeof ChargedWooOrderResultSchema>;
export type SetOrderPointsRequest = z.infer<typeof SetOrderPointsSchema>;
export type OrderPointsDto = z.infer<typeof OrderPointsSchema>;
export type WooOrderResultDto = z.infer<typeof WooOrderResultSchema>;
export type WooOrderRequoteDto = z.infer<typeof WooOrderRequoteSchema>;
export type OrderSummaryDto = z.infer<typeof OrderSummarySchema>;
export type OrderPageDto = z.infer<typeof OrderPageSchema>;
export type OrderDto = z.infer<typeof OrderSchema>;
export type OrderLineDto = z.infer<typeof OrderLineSchema>;
export type InvoiceDto = z.infer<typeof InvoiceSchema>;
export type DeliveryAddressDto = z.infer<typeof DeliveryAddressSchema>;
export type OrderQuery = z.infer<typeof OrderQuerySchema>;
export type CreateOrderRequest = z.infer<typeof CreateOrderSchema>;
export type UpdateOrderRequest = z.infer<typeof UpdateOrderSchema>;
export type SetOrderLinesRequest = z.infer<typeof SetOrderLinesSchema>;
export type PlaceOrderRequest = z.infer<typeof PlaceOrderSchema>;
export type ShipOrderRequest = z.infer<typeof ShipOrderSchema>;
export type OrderReasonRequest = z.infer<typeof OrderReasonSchema>;

const json = <T extends z.ZodType>(schema: T) => ({ 'application/json': { schema } });
const body = <T extends z.ZodType>(schema: T) => ({ required: true, content: json(schema) });
const errors = {
  '400': problemResponse('Invalid input'),
  '401': problemResponse('Not signed in'),
  '403': problemResponse('Missing permission'),
  '404': problemResponse('No such order'),
};
const changed = {
  '200': { description: 'The order after the change', content: json(OrderSchema) },
  ...errors,
  '422': problemResponse('Not allowed in this status (or short stock, blocked customer)'),
};
const id = { path: z.object({ id: z.uuid() }) };

export const orderPaths: ZodOpenApiPathsObject = {
  '/api/v1/orders': {
    get: {
      summary: 'Orders list (orders.edit or shipments.manage); saved views and filters',
      requestParams: { query: OrderQuerySchema },
      responses: { '200': { description: 'OK', content: json(OrderPageSchema) }, ...errors },
    },
    post: {
      summary: 'New draft order for a customer (manager channel)',
      requestBody: body(CreateOrderSchema),
      responses: { '201': { description: 'Draft', content: json(OrderSchema) }, ...errors },
    },
  },
  '/api/v1/orders/from-woo': {
    post: {
      summary:
        'Store order (DeliStore plugin, signed): verify the quote, then create and place the order in one transaction; idempotent by wooOrderId',
      requestBody: body(WooOrderSchema),
      responses: {
        '200': { description: 'Placed (or already there)', content: json(WooOrderResultSchema) },
        '400': problemResponse('Invalid input'),
        '401': problemResponse('Not signed by the store'),
        '403': problemResponse('Missing permission'),
        '409': {
          description: 'The quote expired or no longer matches: show the fresh price and retry',
          content: json(WooOrderRequoteSchema),
        },
        '422': problemResponse('Short stock, blocked customer or no delivery address'),
      },
    },
  },
  '/api/v1/orders/from-woo/charged': {
    post: {
      summary:
        'A store order already charged by the store (spec §7.7; DeliStore plugin, signed): recorded as is, never refused; differences from the CRM go to `attention`. Idempotent by wooOrderId: a repeat only adds a payment not recorded yet',
      requestBody: body(ChargedWooOrderSchema),
      responses: {
        '200': {
          description: 'Recorded (or already there)',
          content: json(ChargedWooOrderResultSchema),
        },
        '400': problemResponse('Invalid input'),
        '401': problemResponse('Not signed by the store'),
        '403': problemResponse('Missing permission'),
        '422': problemResponse('A SKU the CRM does not know: sync the product, then resend'),
      },
    },
  },
  '/api/v1/orders/{id}': {
    get: {
      summary: 'Order card: lines with breakdowns, invoices, payments, return, own audit trail',
      requestParams: id,
      responses: { '200': { description: 'OK', content: json(OrderSchema) }, ...errors },
    },
    patch: {
      summary: 'Delivery, comment, payment method (draft only); a placed order needs a reason',
      requestParams: id,
      requestBody: body(UpdateOrderSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/lines': {
    put: {
      summary:
        'Replace the lines and re-price the order (live quote). Placed orders: reason required, reservation and invoices adjusted',
      requestParams: id,
      requestBody: body(SetOrderLinesSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/points': {
    get: {
      summary: 'Q-points on the order: what it holds, may take, earns or earned',
      requestParams: id,
      responses: { '200': { description: 'OK', content: json(OrderPointsSchema) }, ...errors },
    },
    put: {
      summary:
        'Set the Q-points the order spends (within the limit). Placed orders: reason required, points and invoices adjusted',
      requestParams: id,
      requestBody: body(SetOrderPointsSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/place': {
    post: {
      summary:
        'Place a draft: snapshot prices, reserve stock, issue the invoice — one transaction, safe to retry',
      requestParams: id,
      requestBody: body(PlaceOrderSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/cancel': {
    post: {
      summary:
        'Cancel (draft / awaiting payment / confirmed): releases stock, cancels invoices, refunds what was paid',
      requestParams: id,
      requestBody: body(OrderReasonSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/ship': {
    post: {
      summary: 'Confirmed → Shipped by hand (shipments.manage): stock leaves the warehouse',
      requestParams: id,
      requestBody: body(ShipOrderSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/deliver': {
    post: {
      summary: 'Shipped → Delivered by hand (shipments.manage): the return window starts',
      requestParams: id,
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/return': {
    post: {
      summary: 'Full return: restock, refund everything paid, fiscal return receipt',
      requestParams: id,
      requestBody: body(OrderReasonSchema),
      responses: changed,
    },
  },
  '/api/v1/orders/{id}/invoices/{invoiceId}/pdf': {
    get: {
      summary: 'Invoice as PDF',
      requestParams: { path: z.object({ id: z.uuid(), invoiceId: z.uuid() }) },
      responses: {
        '200': {
          description: 'PDF',
          content: { 'application/pdf': { schema: z.string().meta({ format: 'binary' }) } },
        },
        ...errors,
      },
    },
  },
};
